import React, { useState } from 'react';
import './aluminiumwindow.css';

import {
  FaCity,
  FaThermometerHalf,
  FaShieldAlt,
  FaRegBuilding,
  FaCompressArrowsAlt,
  FaVolumeMute
} from 'react-icons/fa';

function CurtainWalls() {
  const [activeTab, setActiveTab] = useState('types');
  const [selectedImage, setSelectedImage] = useState(0);

  const images = [
    '/curtain wall 1.jpg',
    '/curtain wall 2.webp',
    '/curtain wall 3.jpg',
    '/curtain wall 4.jpg',
    '/curtain wall 5.jpg',
  ];

  const features = [
    {
      icon: <FaCity />,
      title: 'Architectural Elegance',
      description: 'Sleek aluminium curtain wall systems designed for modern high-rise and commercial buildings'
    },
    {
      icon: <FaThermometerHalf />,
      title: 'Thermal Performance',
      description: 'Advanced glazing and insulation technologies to enhance energy efficiency'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Weather Resistance',
      description: 'Engineered to withstand wind loads, rain penetration, and extreme weather conditions'
    },
    {
      icon: <FaRegBuilding />,
      title: 'Structural Stability',
      description: 'Robust aluminium framing systems that support large glass panels safely'
    },
    {
      icon: <FaCompressArrowsAlt />,
      title: 'Large Span Capability',
      description: 'Supports wide glass spans for uninterrupted panoramic views'
    },
    {
      icon: <FaVolumeMute />,
      title: 'Acoustic Insulation',
      description: 'High-performance glass options to minimize external noise'
    }
  ];

  const curtainWallTypes = [
    { name: 'Stick System', image: '/stick system.webp' },
    { name: 'Unitized System', image: '/unitized.jpg' },
    { name: 'Semi-Unitized System', image: '/semi utilize.png' },
    { name: 'Structural Glazing', image: '/structural glazing.webp' },
  ];

  const specifications = [
    { label: 'Frame Material', value: 'High Strength Aluminium Alloy' },
    { label: 'Glass Options', value: 'Double / Triple Glazing' },
    { label: 'Glass Thickness', value: '24mm – 40mm' },
    { label: 'Thermal Performance', value: 'Low-E & Thermal Break Systems' },
    { label: 'Wind Load Resistance', value: 'As per International Standards' },
    { label: 'Sound Reduction', value: 'Up to 45 dB' },
    { label: 'Finish', value: 'Powder Coated / Anodized' },
    { label: 'Warranty', value: '10 Years System Warranty' }
  ];

  return (
    <div className="aluminium-window">

      {/* Hero Section */}
      <section className="aluminium-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Aluminium Curtain Wall Systems</h1>
            <p className="hero-subtitle">
              High-performance façade solutions that combine architectural beauty,
              structural strength, and energy efficiency for modern buildings.
            </p>
          </div>

          <div className="hero-image">
            <img
              src={images[selectedImage]}
              alt="Curtain Wall System"
              className="hero-main-image"
            />
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Curtain Wall ${index + 1}`}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="section-header">
          <h2>Why Choose Our Curtain Walls?</h2>
          <p>Advanced façade engineering for modern architecture</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs */}
      <section className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'types' ? 'active' : ''}`}
            onClick={() => setActiveTab('types')}
          >
            Curtain Wall Types
          </button>

          <button
            className={`tab-btn ${activeTab === 'benefits' ? 'active' : ''}`}
            onClick={() => setActiveTab('benefits')}
          >
            Benefits
          </button>
        </div>

        <div className="tabs-content">

          {activeTab === 'types' && (
            <div className="window-types-grid">
              {curtainWallTypes.map((type, index) => (
                <div key={index} className="window-type-card">
                  <img src={type.image} alt={type.name} />
                  <h4>{type.name}</h4>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'benefits' && (
            <div className="benefits-content">
              <div className="benefit-card">
                <h3>Modern Façade Design</h3>
                <p>Enhances building aesthetics with clean lines and full-glass exteriors.</p>
              </div>
              <div className="benefit-card">
                <h3>Energy Efficiency</h3>
                <p>Reduces heat gain and energy consumption with advanced glazing systems.</p>
              </div>
              <div className="benefit-card">
                <h3>Structural Safety</h3>
                <p>Designed to meet wind load and seismic performance standards.</p>
              </div>
              <div className="benefit-card">
                <h3>Low Maintenance</h3>
                <p>Durable aluminium frames require minimal maintenance over time.</p>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Build with Confidence</h2>
          <p>Talk to our experts for customized curtain wall solutions</p>
          <div className="cta-buttons">
            <a href="/contactus">
              <button className="btn-outline">Contact Us</button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>What is a curtain wall system?</h4>
            <p>A non-structural outer façade that protects buildings from weather while enhancing aesthetics.</p>
          </div>
          <div className="faq-item">
            <h4>Are curtain walls energy efficient?</h4>
            <p>Yes, when combined with thermal breaks and Low-E glass, they significantly reduce energy loss.</p>
          </div>
          <div className="faq-item">
            <h4>Can curtain walls be customized?</h4>
            <p>Yes, systems are fully customizable in design, glass type, finish, and performance.</p>
          </div>
          <div className="faq-item">
            <h4>Where are curtain walls commonly used?</h4>
            <p>High-rise buildings, offices, hotels, malls, and commercial towers.</p>
          </div>
        </div>
      </section>

    </div>
  );
}

export default CurtainWalls;
